/**
 * htmlEmbed.ts — Template V2 HTML / iframe embed normalization.
 *
 * The `html` layout component's value can be:
 *   - a bare `https://` URL  → a single full-bleed `<iframe>`;
 *   - a single `<iframe …></iframe>` snippet → the same structured iframe;
 *   - script-free HTML that may contain one or more iframes → sanitized markup.
 *
 * Security contract: authored `<script>`, `on*` handlers and `javascript:` /
 * `data:` URLs are stripped; only an allowlisted tag/attribute set survives.
 * The output is either a structured iframe (safe to render as `<iframe>` /
 * WebView `source={{ uri }}`) or a sanitized HTML string.
 *
 * This module is intentionally dependency-free (no DOM, no URL global) so it
 * runs on Hermes (Containd app), Node/browser (containd.space) and the editor.
 * The containd.space twin lives at
 * ContaindWebAPI_01/packages/template-render/src/html/htmlEmbed.ts and MUST
 * stay behaviorally identical (see htmlEmbed.parity tests).
 */

export interface NormalizedIframeEmbed {
  kind: 'iframe';
  src: string;
  allow?: string;
  allowFullscreen?: boolean;
  title?: string;
  referrerpolicy?: string;
  loading?: 'lazy' | 'eager';
}

export interface NormalizedHtmlEmbed {
  kind: 'html';
  html: string;
  /** True when the sanitized snippet contains at least one iframe. */
  hasIframe: boolean;
}

export type NormalizedEmbed = NormalizedIframeEmbed | NormalizedHtmlEmbed;

/** First-party hosts that may load without a third-party consent gate. */
export const FIRST_PARTY_EMBED_HOSTS = ['containd.space'] as const;

// ── URL helpers ───────────────────────────────────────────────────────────────

const BARE_URL_RE = /^https:\/\/([^\s/?#<>"']+)([^\s<>"']*)$/i;

/** Extract the lowercase host from an https URL (regex only — no `URL` global). */
export function hostnameOfEmbedUrl(url: string | null | undefined): string | null {
  if (typeof url !== 'string') return null;
  const m = /^https:\/\/(?:[^/@\s]+@)?([^\s/?#:<>"']+)(?::\d+)?/i.exec(url.trim());
  if (!m) return null;
  return m[1].toLowerCase().replace(/\.$/, '');
}

/** True when the iframe host is containd.space (first-party, no consent gate). */
export function isFirstPartyEmbed(url: string | null | undefined): boolean {
  const host = hostnameOfEmbedUrl(url);
  if (!host) return false;
  return FIRST_PARTY_EMBED_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
}

/** Returns a normalized https URL, or null for anything that is not a bare https URL. */
export function parseBareHttpsUrl(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const s = raw.trim();
  if (!BARE_URL_RE.test(s)) return null;
  return s;
}

// ── Embed frame background ────────────────────────────────────────────────────

/**
 * Resolve the opaque background color for a rendered embed frame.
 *
 * A script-free HTML snippet's sandboxed `srcDoc` document is transparent by
 * default, so the poster layer behind it (item face + scrim + icon/text) shows
 * through. The snippet frame must therefore paint the component background.
 *
 * Alpha-sensitive: only a FULLY OPAQUE color is painted into the frame document.
 * A translucent component background must NOT be flattened to a solid color — it
 * would darken whatever sits behind the canvas item. For a translucent value the
 * frame stays transparent and the poster layer underneath is painted with the
 * component background at full alpha instead (see `resolveEmbedPosterBackground`).
 *
 * Returns a `#rrggbb` string when opaque, else null (paint transparent).
 */
export function resolveEmbedFrameBackground(
  backgroundColor: string | null | undefined,
): string | null {
  if (typeof backgroundColor !== 'string') return null;
  const raw = backgroundColor.trim();
  if (!raw) return null;

  // #rgb / #rrggbb (no alpha channel → opaque).
  const hex = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(raw);
  if (hex) {
    const digits = hex[1];
    if (digits.length === 3) {
      return `#${digits[0]}${digits[0]}${digits[1]}${digits[1]}${digits[2]}${digits[2]}`.toLowerCase();
    }
    return `#${digits.toLowerCase()}`;
  }
  // #rgba / #rrggbbaa → opaque only when alpha is fully opaque.
  const hexA = /^#([0-9a-fA-F]{4}|[0-9a-fA-F]{8})$/.exec(raw);
  if (hexA) {
    const d = hexA[1];
    const alpha = d.length === 4 ? d[3] : d.slice(6);
    const opaque = alpha === 'f' || alpha === 'F' || alpha === 'ff' || alpha === 'FF';
    if (!opaque) return null;
    const rgb = d.length === 4 ? `${d[0]}${d[0]}${d[1]}${d[1]}${d[2]}${d[2]}` : d.slice(0, 6);
    return `#${rgb.toLowerCase()}`;
  }

  const rgb = /^rgba?\(([^)]+)\)$/i.exec(raw);
  if (rgb) {
    const parts = rgb[1].split(',').map((p) => p.trim());
    if (parts.length < 3) return null;
    const [r, g, b] = parts;
    const a = parts.length >= 4 ? Number(parts[3]) : 1;
    if (!(a >= 1)) return null; // translucent (or NaN) → transparent frame
    return `rgb(${r}, ${g}, ${b})`;
  }

  // Named colors / other formats: not doing color math — treat as opaque and
  // let the browser resolve it (a bad value is simply ignored by CSS).
  return raw;
}

/**
 * Resolve the poster-layer background used while an HTML snippet frame is live.
 *
 * For a translucent component background we cannot paint the frame opaque, so
 * the poster layer behind the (transparent) snippet frame is painted with the
 * component background at FULL alpha — the composite is exactly the component
 * background the user configured, without darkening the layer behind the item.
 * Fully opaque backgrounds paint the frame directly (frame covers the poster).
 *
 * Returns the CSS color (with alpha) to paint on the poster layer, or null when
 * the frame itself is opaque and the poster must not be painted.
 */
export function resolveEmbedPosterBackground(
  backgroundColor: string | null | undefined,
): string | null {
  if (resolveEmbedFrameBackground(backgroundColor) !== null) return null;
  if (typeof backgroundColor !== 'string') return null;
  const raw = backgroundColor.trim();
  if (!raw) return null;
  return raw;
}

// ── Frame rendering policy ────────────────────────────────────────────────────

/** Sandbox for a snippet frame that does NOT embed another iframe. */
export const HTML_EMBED_SANDBOX_STRICT =
  'allow-scripts allow-presentation allow-popups allow-forms allow-popups-to-escape-sandbox';

/**
 * Sandbox for a snippet frame that embeds at least one cross-origin iframe.
 *
 * A sandboxed `srcDoc` document without `allow-same-origin` has an opaque (null)
 * origin. A nested iframe whose target restricts framing (`Content-Security-Policy:
 * frame-ancestors`, not a bare `*`) then rejects the opaque ancestor and the frame
 * is blocked. Granting `allow-same-origin` restores the real ancestor origin. The
 * snippet itself is script-free (sanitizer contract) and nested frames run on their
 * own origins, so this does not widen the nested frames' reach.
 */
export const HTML_EMBED_SANDBOX_WITH_FRAMES =
  'allow-scripts allow-same-origin allow-presentation allow-popups allow-forms allow-popups-to-escape-sandbox';

/** Pick the srcDoc sandbox based on whether the snippet embeds an iframe. */
export function resolveHtmlEmbedSandbox(hasIframe: boolean): string {
  return hasIframe ? HTML_EMBED_SANDBOX_WITH_FRAMES : HTML_EMBED_SANDBOX_STRICT;
}

/**
 * Add `credentialless` to every nested `<iframe>` of a sanitized snippet.
 *
 * The Containd web page sends `Cross-Origin-Embedder-Policy: credentialless`
 * (expo-sqlite / SharedArrayBuffer). Under COEP a cross-origin frame without a
 * matching `Cross-Origin-Resource-Policy` is blocked unless the frame element
 * carries `credentialless`. The lone-iframe path sets it on the outer frame;
 * nested frames inside a snippet must carry it too.
 *
 * Idempotent: an opening tag that already advertises `credentialless` is left
 * untouched. Authored `credentialless` never survives the sanitizer, so this is
 * also the only way it reaches a nested frame.
 */
export function withCredentiallessIframes(html: string): string {
  if (!html) return html;
  return html.replace(/<iframe(?![^>]*\bcredentialless\b)/gi, '<iframe credentialless');
}

/** First-party https origin used as the native snippet WebView document base. */
export const HTML_EMBED_NATIVE_BASE_URL = 'https://containd.space/';

/**
 * `baseUrl` for the native (react-native-webview) snippet document.
 *
 * The WebView defaults to `about:blank`, which gives the document an opaque
 * origin — the same `frame-ancestors` problem as the web `srcDoc` wrapper. A
 * snippet that embeds an iframe gets a real first-party https origin; an
 * iframe-less snippet keeps the default.
 */
export function resolveHtmlEmbedWebViewBaseUrl(
  embed: NormalizedEmbed | null | undefined,
): string | undefined {
  return embed?.kind === 'html' && embed.hasIframe ? HTML_EMBED_NATIVE_BASE_URL : undefined;
}

// ── Allowlists ────────────────────────────────────────────────────────────────

const ALLOWED_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'caption',
  'code',
  'div',
  'em',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'iframe',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

const VOID_TAGS = new Set(['br', 'hr', 'img']);

/** Raw-text elements whose entire body is dropped (never emitted). */
const DROPPED_RAW_TAGS = new Set(['script', 'style', 'textarea', 'title', 'xmp', 'noscript', 'template']);

const GLOBAL_ATTRS = new Set(['class', 'dir', 'lang', 'title']);

const TAG_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'target', 'rel']),
  img: new Set(['src', 'alt', 'width', 'height', 'loading', 'decoding']),
  iframe: new Set([
    'src',
    'allow',
    'allowfullscreen',
    'title',
    'referrerpolicy',
    'loading',
    'width',
    'height',
    'frameborder',
  ]),
  ol: new Set(['start', 'type']),
  td: new Set(['colspan', 'rowspan', 'align']),
  th: new Set(['colspan', 'rowspan', 'align', 'scope']),
};

/** Attributes that are always dropped, regardless of element. */
const BLOCKED_ATTRS = new Set([
  'srcdoc',
  'formaction',
  'xlink:href',
  'xmlns',
  'http-equiv',
  'content',
]);

const URL_ATTRS: Record<string, 'href' | 'image' | 'iframe'> = {
  href: 'href',
  src: 'image',
};

function isSafeUrlValue(value: string, kind: 'href' | 'image' | 'iframe'): boolean {
  const v = value.trim();
  if (!v) return false;
  if (/[\u0000-\u001f]/.test(v)) return false;
  const lower = v.toLowerCase();
  if (lower.startsWith('javascript:') || lower.startsWith('vbscript:')) return false;
  if (lower.startsWith('data:')) {
    // Only raster images may be inlined.
    return kind === 'image' && /^data:image\/(png|jpe?g|gif|webp|avif);base64,/i.test(v);
  }
  if (kind === 'iframe') return lower.startsWith('https:');
  if (lower.startsWith('https:')) return true;
  if (kind === 'image') return lower.startsWith('http:');
  // href
  return lower.startsWith('http:') || lower.startsWith('mailto:') || lower.startsWith('tel:') || v.startsWith('#') || v.startsWith('/');
}

// ── Scanner ───────────────────────────────────────────────────────────────────

interface IframeSpec extends NormalizedIframeEmbed {}

interface ScanResult {
  html: string;
  iframeCount: number;
  otherElementCount: number;
  hasText: boolean;
  firstIframe: IframeSpec | null;
}

interface Attr {
  name: string;
  value: string | null;
}

const TAG_NAME_RE = /^<(\/?)([a-zA-Z][a-zA-Z0-9-]*)/;
const ATTR_RE = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'`=<>]+)))?/g;

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function parseAttrs(source: string): Attr[] {
  const attrs: Attr[] = [];
  ATTR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = ATTR_RE.exec(source)) !== null) {
    const name = m[1].toLowerCase();
    const value = m[2] ?? m[3] ?? m[4] ?? null;
    attrs.push({ name, value });
    if (m.index === ATTR_RE.lastIndex) ATTR_RE.lastIndex += 1;
  }
  return attrs;
}

/** Find the index just past the `>` that closes a tag starting at `start` (respects quotes). */
function findTagEnd(input: string, start: number): number {
  let quote: string | null = null;
  for (let i = start; i < input.length; i += 1) {
    const ch = input[i];
    if (quote) {
      if (ch === quote) quote = null;
    } else if (ch === '"' || ch === "'") {
      quote = ch;
    } else if (ch === '>') {
      return i + 1;
    }
  }
  return -1;
}

/** Skip a raw-text element body, returning the index just past its close tag. */
function skipRawText(input: string, afterOpenTag: number, tagName: string): number {
  const closeRe = new RegExp(`</${tagName}\\s*>`, 'i');
  closeRe.lastIndex = 0;
  const rest = input.slice(afterOpenTag);
  const m = closeRe.exec(rest);
  return m ? afterOpenTag + m.index + m[0].length : input.length;
}

function scanHtml(input: string): ScanResult {
  const out: string[] = [];
  const result: ScanResult = {
    html: '',
    iframeCount: 0,
    otherElementCount: 0,
    hasText: false,
    firstIframe: null,
  };

  let i = 0;
  while (i < input.length) {
    const lt = input.indexOf('<', i);
    if (lt === -1) {
      const text = input.slice(i);
      out.push(escapeText(text));
      if (text.trim()) result.hasText = true;
      break;
    }
    if (lt > i) {
      const text = input.slice(i, lt);
      out.push(escapeText(text));
      if (text.trim()) result.hasText = true;
    }

    // Comments / doctypes / processing instructions — drop.
    if (input.startsWith('<!--', lt)) {
      const end = input.indexOf('-->', lt + 4);
      i = end === -1 ? input.length : end + 3;
      continue;
    }
    if (input[lt + 1] === '!' || input[lt + 1] === '?') {
      const end = findTagEnd(input, lt + 2);
      i = end === -1 ? input.length : end;
      continue;
    }

    const tagMatch = TAG_NAME_RE.exec(input.slice(lt, lt + 80));
    if (!tagMatch) {
      // Bare `<` that is not a tag — emit escaped and advance one char.
      out.push('&lt;');
      i = lt + 1;
      continue;
    }

    const isClose = tagMatch[1] === '/';
    const tagName = tagMatch[2].toLowerCase();
    const tagEnd = findTagEnd(input, lt + tagMatch[0].length);
    if (tagEnd === -1) {
      out.push('&lt;');
      i = lt + 1;
      continue;
    }
    const tagBody = input.slice(lt + tagMatch[0].length, tagEnd - 1);

    if (!isClose && DROPPED_RAW_TAGS.has(tagName)) {
      i = skipRawText(input, tagEnd, tagName);
      continue;
    }

    if (!ALLOWED_TAGS.has(tagName)) {
      // Unknown tag: drop the markers, keep inner text.
      result.otherElementCount += 1;
      i = tagEnd;
      continue;
    }

    if (isClose) {
      if (VOID_TAGS.has(tagName)) {
        i = tagEnd;
        continue;
      }
      if (tagName !== 'iframe') result.otherElementCount += 1;
      out.push(`</${tagName}>`);
      i = tagEnd;
      continue;
    }

    const parsed = parseAttrs(tagBody);
    const allowedForTag = TAG_ATTRS[tagName];
    const safe: Attr[] = [];
    for (const attr of parsed) {
      if (BLOCKED_ATTRS.has(attr.name)) continue;
      if (attr.name.startsWith('on')) continue;
      if (attr.name === 'style') continue;
      const globalOk = GLOBAL_ATTRS.has(attr.name);
      const tagOk = allowedForTag?.has(attr.name) ?? false;
      if (!globalOk && !tagOk) continue;
      const urlKind = tagName === 'iframe' && attr.name === 'src' ? 'iframe' : URL_ATTRS[attr.name];
      if (urlKind) {
        if (attr.value == null || !isSafeUrlValue(attr.value, urlKind)) continue;
      }
      safe.push(attr);
    }

    if (tagName === 'iframe') {
      // Non-eager (lazy) embeds pasted verbatim from providers (e.g. Elfsight)
      // carry the URL in `data-src` with an empty/missing `src`. Promote a safe
      // https `data-src` to `src`; `data-src` itself is never emitted.
      let srcAttr = safe.find((a) => a.name === 'src');
      if (!srcAttr || !srcAttr.value || !isSafeUrlValue(srcAttr.value, 'iframe')) {
        const lazy = parsed.find((a) => a.name === 'data-src');
        if (lazy?.value && isSafeUrlValue(lazy.value, 'iframe')) {
          srcAttr = { name: 'src', value: lazy.value.trim() };
          const idx = safe.findIndex((a) => a.name === 'src');
          if (idx >= 0) safe[idx] = srcAttr;
          else safe.push(srcAttr);
        }
      }
      if (!srcAttr || !srcAttr.value || !srcAttr.value.toLowerCase().startsWith('https:')) {
        // An iframe without a safe https src is dropped entirely.
        i = tagEnd;
        // Skip its closing tag if present.
        const closeRe = /<\/iframe\s*>/i;
        closeRe.lastIndex = 0;
        const rest = input.slice(i);
        const cm = closeRe.exec(rest);
        if (cm) i += cm.index + cm[0].length;
        continue;
      }
      result.iframeCount += 1;

      const attrsHtml = safe
        .filter((a) => a.name !== 'width' && a.name !== 'height' && a.name !== 'frameborder')
        .map((a) => (a.value == null ? a.name : `${a.name}="${escapeAttr(a.value)}"`))
        .join(' ');
      out.push(`<iframe${attrsHtml ? ` ${attrsHtml}` : ''}></iframe>`);
      // Ensure closing tag is not emitted twice.
      const closeRe = /<\/iframe\s*>/i;
      closeRe.lastIndex = 0;
      const rest = input.slice(tagEnd);
      const cm = closeRe.exec(rest);
      if (cm) {
        i = tagEnd + cm.index + cm[0].length;
      } else {
        i = tagEnd;
      }

      if (!result.firstIframe) {
        const get = (name: string): string | undefined => safe.find((a) => a.name === name)?.value ?? undefined;
        const loading = get('loading')?.toLowerCase();
        result.firstIframe = {
          kind: 'iframe',
          src: srcAttr.value,
          ...(get('allow') ? { allow: get('allow') } : {}),
          ...(safe.some((a) => a.name === 'allowfullscreen') ? { allowFullscreen: true } : {}),
          ...(get('title') ? { title: get('title') } : {}),
          ...(get('referrerpolicy') ? { referrerpolicy: get('referrerpolicy') } : {}),
          ...(loading === 'lazy' || loading === 'eager' ? { loading } : {}),
        };
      }
      continue;
    }

    if (tagName === 'img' && !safe.some((a) => a.name === 'src')) {
      // An image without a safe source is meaningless — drop it.
      i = tagEnd;
      continue;
    }

    const attrsHtml = safe
      .map((a) => (a.value == null ? a.name : `${a.name}="${escapeAttr(a.value)}"`))
      .join(' ');
    result.otherElementCount += 1;
    out.push(`<${tagName}${attrsHtml ? ` ${attrsHtml}` : ''}>`);
    i = tagEnd;
  }

  result.html = out.join('').trim();
  return result;
}

function escapeText(text: string): string {
  return text
    .replace(/&(?!(?:[a-zA-Z][a-zA-Z0-9]{1,31}|#\d{1,7}|#x[0-9a-fA-F]{1,6});)/g, '&amp;')
    .replace(/</g, '&lt;');
}

/** Sanitize an HTML fragment to the allowlisted subset. Returns '' when nothing survives. */
export function sanitizeHtmlFragment(input: string): string {
  return scanHtml(input).html;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Normalize an authored embed value into a renderable form.
 * Returns null when empty, not a URL, or nothing safe survives sanitization.
 */
export function normalizeHtmlEmbed(raw: string | null | undefined): NormalizedEmbed | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  // Bare URL (no markup at all).
  if (!/[<>]/.test(trimmed)) {
    const src = parseBareHttpsUrl(trimmed);
    return src ? { kind: 'iframe', src } : null;
  }

  const scan = scanHtml(trimmed);
  if (!scan.html) return null;

  // Only a single iframe (ignoring whitespace) → structured, full-bleed iframe.
  if (scan.iframeCount === 1 && scan.otherElementCount === 0 && !scan.hasText && scan.firstIframe) {
    return scan.firstIframe;
  }

  return { kind: 'html', html: scan.html, hasIframe: scan.iframeCount > 0 };
}

// ── Serialization / one-shot sanitize ─────────────────────────────────────────

/** Serialize a normalized embed back to a canonical HTML string (idempotent). */
export function serializeNormalizedEmbed(embed: NormalizedEmbed): string {
  if (embed.kind === 'html') return embed.html;
  const attrs = [
    `src="${escapeAttr(embed.src)}"`,
    ...(embed.allow ? [`allow="${escapeAttr(embed.allow)}"`] : []),
    ...(embed.allowFullscreen ? ['allowfullscreen'] : []),
    ...(embed.title ? [`title="${escapeAttr(embed.title)}"`] : []),
    ...(embed.referrerpolicy ? [`referrerpolicy="${escapeAttr(embed.referrerpolicy)}"`] : []),
    ...(embed.loading ? [`loading="${embed.loading}"`] : []),
  ];
  return `<iframe ${attrs.join(' ')}></iframe>`;
}

/**
 * Sanitize an authored embed value to a canonical, storage-safe string
 * (used at public-share freeze bake). Returns null when nothing safe remains.
 */
export function sanitizeHtmlEmbedValue(raw: string | null | undefined): string | null {
  const normalized = normalizeHtmlEmbed(raw);
  return normalized ? serializeNormalizedEmbed(normalized) : null;
}
